import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { useContext, useState } from "react";
import { addPaymentMethod } from "../PlanService";
import notifiers from "global/constants/NotificationConstants";
import {
  openErrorNotification,
  isTruthy,
  openSuccessNotification,
  handleErrorMessage,
} from "helpers/methods";
import { MyPlanContext } from "../Context";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

interface CustomProps {
  isDialogOpen: boolean;
  setDialogOpen: Function;
  doesNewPaymentMethodAdded: boolean;
  setDoesNewPaymentMethodAdded: Function;
}

export const cardStyle = {
  style: {
    base: {
      color: "#00000",
      fontFamily: "Inter, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#475569",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const AddPaymentMethodDialog = (props: CustomProps) => {
  const theme = useTheme();
  const [defaultCardStatus, setDefaultCardStatus] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: any) => {
    try {
      setIsLoading(true);
      if (stripe !== null && elements !== null) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement)!,
        });
        if (error) {
          openErrorNotification(
            isTruthy(error) ? error.message : notifiers.GENERIC_ERROR
          );
          setIsLoading(false);
        } else {
          await addCardHandler(paymentMethod?.id);
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const addCardHandler = async (id: any) => {
    try {
      setIsLoading(true);
      await addPaymentMethod(id, defaultCardStatus);
      openSuccessNotification("Payment method added successfully");
      handlePaymentMethodClose();
      props.setDoesNewPaymentMethodAdded(true);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handlePaymentMethodClose = () => {
    setDefaultCardStatus(false);
    props.setDialogOpen(false);
    props.setDoesNewPaymentMethodAdded(false);
  };

  const getTitle = () => {
    return (
      <Stack pt={3}>
        <Box display={"flex"} justifyContent={"center"}>
          <Typography variant="h4" color={theme.palette.primary.main}>
            Payment Method
          </Typography>
        </Box>
      </Stack>
    );
  };

  const getBody = () => {
    return (
      <Stack direction="column" px={2}>
        <Stack>
          <Box
            sx={{
              border: "#47556940 solid 1px",
              borderRadius: "9px",
              padding: "12px",
              backgroundColor: "#fff",
            }}
          >
            <CardElement options={cardStyle} id="card_number" />
          </Box>
        </Stack>
        <Stack>
          <FormControlLabel
            value="male"
            control={
              <Checkbox
                onChange={(e: any) => {
                  setDefaultCardStatus(e.target.checked);
                }}
                checked={defaultCardStatus}
                sx={{
                  paddingLeft: "12px",
                  paddingRight: "4px",
                  "&.Mui-checked": {
                    color: "#BB0070",
                  },
                }}
                id="set_default_card_checkbox"
              />
            }
            label="Set as default"
          />
        </Stack>
        <Stack pt={1}>
          <Typography variant="body2">
            By adding your card here and marking it as the default, you
            authorize the system to automatically charge this default card for
            any future payments.
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={2} mb={1}>
        <Button
          id="contacts_add__cancel_btn"
          variant={"outlined"}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
          onClick={() => handlePaymentMethodClose()}
        >
          Cancel
        </Button>
        <Button
          id="contacts_add_save_btn"
          variant={"contained"}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
          onClick={(e: any) => handleSubmit(e)}
        >
          Save
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomDialog
        isDialogOpen={props.isDialogOpen}
        handleDialogClose={() => handlePaymentMethodClose()}
        title={getTitle()}
        body={getBody()}
        footer={getFooter()}
        closable
        closeButtonVisibility
        persistOnClickOutside
        persistOnEsc
      />
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default AddPaymentMethodDialog;
